import React from 'react';
import {
  Box,
  Card,
  Table,
  Divider,
  TableRow,
  TableBody,
  TableCell,
  TableContainer,
  Typography,
  Chip,
  Stack,
  IconButton,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../redux/store';

import Scrollbar from '../../../../components/scrollbar';
import Iconify from '../../../../components/iconify';

// You can remove Iconify, Avatar, and TableHeadCustom imports if unused
// import {
//   increaseQuantity,
//   decreaseQuantity,
//   deleteCart,
// } from '../../../../redux/slices/product';

// Enhanced types - Define locally to avoid import issues
interface ExtractedItem {
  productId: string;
  productName: string;
  price: number;
  size: number;
  quantity: number;
  addOns: string[];
  selections?: any[];
  specialInstructions: string | null;
  error: string[];
}

interface CompletedTrioItem {
  productId: string;
  productName: string;
  trioPrice: number;
  quantity: number;
  selections: { [groupId: string]: Array<{ item_id: string; name: string; price: number }> };
  totalPrice: number;
}

interface Props {
  title?: string;
  subheader?: string;
  cartData: ExtractedItem[];
  trioData?: CompletedTrioItem[];
  onRemoveSolo?: (productId: string) => void;
  onRemoveTrio?: (productId: string) => void;
}

export default function BookingDetails({
  title = '',
  subheader = '',
  cartData,
  trioData = [],
  onRemoveSolo,
  onRemoveTrio,
  ...other
}: Props) {


  // Helper to format a number as currency
  const fCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);

  // Calculate solo items with subtotals
  const soloItemsWithSubtotal = cartData.map((item) => ({
    ...item,
    subtotal: (item.price * item.quantity),
  }));

  // Calculate totals
  const soloSubtotal = soloItemsWithSubtotal.reduce((sum, item) => sum + item.subtotal, 0);
  const trioSubtotal = trioData.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
  const grandSubtotal = soloSubtotal + trioSubtotal;

  const { organization } = useSelector((state: RootState) => state.organization);

  const fedRate = organization?.federalTaxRate ?? 5.0;
  const provRate = organization?.provincialTaxRate ?? 9.975;
  const province = organization?.province || 'Quebec';

  const tps = grandSubtotal * (fedRate / 100);
  const tvq = grandSubtotal * (provRate / 100);
  const totalTax = tps + tvq;
  const grandTotal = grandSubtotal + totalTax;

  // Dynamic Labeling
  const isFrench = true; // Assuming French based on the UI
  const fedLabel = isFrench ? 'TPS' : 'GST';
  let provLabel = isFrench ? 'TVQ' : 'QST';

  if (province !== 'Quebec') {
    if (['Ontario', 'New Brunswick', 'Newfoundland and Labrador', 'Nova Scotia', 'Prince Edward Island'].includes(province)) {
      provLabel = isFrench ? 'TVH' : 'HST';
    } else {
      provLabel = isFrench ? 'TVP' : 'PST';
    }
  }

  return (
    <Card
      {...other}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          bgcolor: '#f2f7ff',
          color: '#1976d2',
          px: 2,
          py: 1,
          display: 'inline-block',
          borderRadius: '8px 8px 0 0',
          mb: 1
        }}
      >
        <Typography variant="body1" fontWeight={700} sx={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
          🛒 Votre commande
        </Typography>
      </Box>

      <TableContainer sx={{ overflow: 'unset', flexGrow: 1 }}>
        <Scrollbar>
          <Table sx={{ minWidth: 720, height: '100%' }}>
            <TableBody sx={{ height: '100%' }}>
              {soloItemsWithSubtotal.length === 0 && trioData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" sx={{ py: 3 }}>
                      Votre panier est vide.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {/* Solo Items Section */}
                  {soloItemsWithSubtotal.length > 0 && (
                    <>
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1976d2', py: 1, fontSize: '1.15rem' }}>
                            🍔 Articles Solo
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {soloItemsWithSubtotal.map((row, index) => (
                        <SoloItemRow
                          key={`${row.productId}-${index}`}
                          row={row}
                          fCurrency={fCurrency}
                          onRemove={onRemoveSolo}
                        />
                      ))}
                    </>
                  )}

                  {/* Trio Items Section */}
                  {trioData.length > 0 && (
                    <>
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: '#d32f2f', py: 1, fontSize: '1.15rem' }}>
                            🍱 Articles Trio
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {trioData.map((trioItem, index) => (
                        <TrioItemRow
                          key={`${trioItem.productId}-${index}`}
                          trioItem={trioItem}
                          fCurrency={fCurrency}
                          onRemove={onRemoveTrio}
                        />
                      ))}
                    </>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </Scrollbar>
      </TableContainer>

      <Divider />

      <Box
        sx={{
          p: 2,
          bgcolor: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.5,
          mt: 'auto'
        }}
      >
        <Stack direction="row" spacing={4} justifyContent="space-between" sx={{ width: '100%', maxWidth: 300 }}>
          <Typography variant="body1" sx={{ color: '#1976d2', fontWeight: 500 }}>
            Montant
          </Typography>
          <Typography variant="body1" sx={{ color: '#1976d2', fontWeight: 700 }}>
            {fCurrency(grandSubtotal)}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={4} justifyContent="space-between" sx={{ width: '100%', maxWidth: 300 }}>
          <Typography variant="body1" sx={{ color: '#1976d2', fontWeight: 500 }}>
            Taxes
          </Typography>
          <Typography variant="body1" sx={{ color: '#1976d2', fontWeight: 700 }}>
            {fCurrency(totalTax)}
          </Typography>
        </Stack>

        <Divider sx={{ width: '100%', maxWidth: 300, my: 1, borderStyle: 'dashed' }} />

        <Stack direction="row" spacing={4} justifyContent="space-between" sx={{ width: '100%', maxWidth: 300 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#333' }}>
            TOTAL FINAL
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#333' }}>
            {fCurrency(grandTotal)}
          </Typography>
        </Stack>
      </Box>
    </Card>
  );
}

// ────────────────────────────────────────────────

interface SoloItemRowProps {
  row: ExtractedItem & { subtotal: number };
  fCurrency: (val: number) => string;
  onRemove?: (productId: string) => void;
}

function SoloItemRow({ row, fCurrency, onRemove }: SoloItemRowProps) {
  const { productName, quantity, subtotal, addOns = [], selections = [] } = row;

  const allOptions = [
    ...addOns,
    ...selections.map((s: any) => s.name)
  ].filter(Boolean);

  return (
    <TableRow sx={{ '& td': { borderBottom: '1px solid #f8f8f8' } }}>
      <TableCell sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', color: '#444' }}>
              <Box component="span" sx={{ fontWeight: 700, color: '#1976d2', mr: 1 }}>{quantity}x</Box>
              {productName}
            </Typography>
            {allOptions.length > 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                {allOptions.join(' | ')}
              </Typography>
            )}
          </Box>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#333' }}>
            {fCurrency(subtotal)}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
}

// ────────────────────────────────────────────────

interface TrioItemRowProps {
  trioItem: CompletedTrioItem;
  fCurrency: (val: number) => string;
  onRemove?: (productId: string) => void;
}

function TrioItemRow({ trioItem, fCurrency, onRemove }: TrioItemRowProps) {
  const { productName, totalPrice, quantity = 1, selections = {} } = trioItem;

  const trioOptions = Object.values(selections)
    .flat()
    .map((s: any) => s.name)
    .filter(Boolean);

  return (
    <TableRow sx={{ '& td': { borderBottom: '1px solid #f8f8f8' } }}>
      <TableCell sx={{ py: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500, fontSize: '1rem', color: '#444' }}>
              <Box component="span" sx={{ fontWeight: 700, color: '#d32f2f', mr: 1 }}>{quantity}x</Box>
              {productName} <Box component="span" sx={{ fontSize: '0.8rem', color: '#d32f2f', ml: 1 }}>Trio</Box>
            </Typography>
            {trioOptions.length > 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                {trioOptions.join(' | ')}
              </Typography>
            )}
          </Box>
          <Typography variant="body1" sx={{ fontWeight: 700, color: '#333' }}>
            {fCurrency(totalPrice)}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );
}
